import { parseAsArrayOf, parseAsNumberLiteral, parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';

import type { JobPostLocationType, JobPostRegion } from '~/api/jobPosts/types';

import { jobPostLocationTypes, jobPostRegions } from '~/api/jobPosts/constants';
import { availableActivities } from '~/constants/constants';
import { SearchParamKeys } from '~/constants/url';
import { parseAsPagination } from '~/utils/nuqs';

import { persistJobPostFiltersInSession, retrieveJobPostFiltersFromSession } from './JobPosts.utils';

export const useJobPostPagination = () => useQueryState(SearchParamKeys.pagination, parseAsPagination);

export const useJobPostFilters = () => {
  const [{ activityIds, locationType, regions }, setFilters] = useQueryStates(
    {
      activityIds: parseAsArrayOf(parseAsNumberLiteral(availableActivities)).withDefault([]),
      regions: parseAsArrayOf(parseAsStringLiteral(jobPostRegions)).withDefault([]),
      locationType: parseAsStringLiteral(jobPostLocationTypes),
    },
    {
      urlKeys: {
        activityIds: SearchParamKeys.FILTER_ACTIVITY_ID,
        regions: SearchParamKeys.FILTER_REGION,
        locationType: SearchParamKeys.FILTER_LOCATION_TYPE,
      },
    },
  );

  const [hasRestoredInitialFilters, setHasRestoredInitialFilters] = useState(false);

  useEffect(() => {
    const initialFilters = retrieveJobPostFiltersFromSession();
    if (initialFilters) {
      setFilters({
        activityIds: initialFilters.activityIds,
        locationType: initialFilters.locationType,
        regions: initialFilters.regions,
      });
    }
    setHasRestoredInitialFilters(true);
  }, [setFilters]);

  useEffect(() => {
    if (!hasRestoredInitialFilters) return;
    persistJobPostFiltersInSession({ activityIds, locationType, regions });
  }, [activityIds, locationType, regions, hasRestoredInitialFilters]);

  return useMemo(
    () => ({
      activityIds: {
        value: activityIds,
        onChange: (value: typeof availableActivities) => setFilters({ activityIds: value }),
      },
      locationType: {
        value: locationType,
        onChange: (value: JobPostLocationType | null) => setFilters({ locationType: value }),
      },
      regions: {
        value: regions,
        onChange: (value: JobPostRegion[]) => setFilters({ regions: value }),
      },
      hasSomeActive: activityIds.length > 0 || !!locationType || regions.length > 0,
      reset: () =>
        setFilters({
          activityIds: [],
          locationType: null,
          regions: [],
        }),
    }),
    [activityIds, locationType, regions, setFilters],
  );
};
